import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { GitMerge, Minus, Plus, BookOpen, Search, Library } from "lucide-react";
import { Deck, Card } from "@/types/linguadeck";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

interface MergeModulesModalProps {
  open: boolean;
  onClose: () => void;
  currentDeck: Deck;
  allDecks: Deck[];
  addDeck: (deck: Deck) => Promise<Deck>;
  onMerged: (created: Deck) => void;
}

function deepCopyCard(c: Card): Card {
  const { id: _id, ...rest } = c;
  return { ...rest };
}

export default function MergeModulesModal({
  open,
  onClose,
  currentDeck,
  allDecks,
  addDeck,
  onMerged,
}: MergeModulesModalProps) {
  const { t } = useTranslation();
  const [selectedModules, setSelectedModules] = useState<Deck[]>([]);
  const [mergeTitle, setMergeTitle] = useState("");
  const [isMerging, setIsMerging] = useState(false);
  const [availableSearch, setAvailableSearch] = useState("");

  const selectedIds = new Set(selectedModules.map((d) => d.id));
  // Available = full user library minus selected (allDecks is the complete library from state)
  const availableModulesBase = allDecks.filter((d) => !selectedIds.has(d.id));
  // Global search: filter across deck titles AND card front/back (entire user library)
  const availableModulesFiltered = useMemo(() => {
    const q = availableSearch.trim().toLowerCase();
    if (!q) return availableModulesBase;
    return availableModulesBase.filter((deck) => {
      const titleMatch = deck.title.toLowerCase().includes(q);
      const cardMatch = (deck.cards ?? []).some(
        (c) =>
          (c.front ?? "").toLowerCase().includes(q) ||
          (c.back ?? "").toLowerCase().includes(q)
      );
      return titleMatch || cardMatch;
    });
  }, [availableModulesBase, availableSearch]);
  const totalCards = selectedModules.reduce((sum, d) => sum + (d.cards?.length ?? 0), 0);

  // Reset selection when modal opens: start with current deck only
  useEffect(() => {
    if (open && currentDeck) {
      setSelectedModules([currentDeck]);
      setAvailableSearch("");
    }
  }, [open, currentDeck?.id]);

  // Auto-fill title: "Объединённый: [Deck1] | [Deck2] | [Deck3]" (i18n prefix + pipe-separated)
  useEffect(() => {
    if (selectedModules.length > 0) {
      const prefix = t("merge_modules.merged_prefix");
      const joined = selectedModules.map((d) => d.title).join(" | ");
      setMergeTitle(prefix + joined);
    }
  }, [selectedModules, t]);

  const addModule = useCallback((deck: Deck) => {
    setSelectedModules((prev) => {
      if (prev.some((d) => d.id === deck.id)) return prev;
      return [...prev, deck];
    });
  }, []);

  const removeModule = useCallback((deckId: string) => {
    setSelectedModules((prev) => prev.filter((d) => d.id !== deckId));
  }, []);

  const handleMerge = useCallback(async () => {
    if (selectedModules.length === 0) return;
    const defaultTitle =
      t("merge_modules.merged_prefix") +
      selectedModules.map((d) => d.title).join(" | ");
    const title = mergeTitle.trim() || defaultTitle;
    setIsMerging(true);
    try {
      const mergedCards: Card[] = selectedModules.flatMap((d) =>
        (d.cards || []).map(deepCopyCard)
      );
      const first = selectedModules[0];
      const payload: Omit<Deck, "id"> = {
        title,
        lang: first.lang,
        emoji: first.emoji,
        description: first.description,
        level: first.level,
        type: first.type,
        deckType: first.deckType,
        studyModes: first.studyModes,
        cards: mergedCards,
      };
      const created = await addDeck(payload as Deck);
      onMerged(created);
      onClose();
    } catch (err) {
      console.error("[MergeModulesModal] Merge failed:", err);
    } finally {
      setIsMerging(false);
    }
  }, [selectedModules, mergeTitle, addDeck, onMerged, onClose, t]);

  if (!currentDeck) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-4xl max-h-[90vh] flex flex-col gap-5 p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge size={22} className="shrink-0 text-purple-500 dark:text-purple-400" />
            {t("mergeModal.title")}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 flex-1 min-h-0">
          {/* Left: Selected for merge */}
          <div className="flex flex-col rounded-xl border border-border bg-muted/20 overflow-hidden min-h-[320px]">
            <div className="px-4 py-3 border-b border-border bg-muted/30 shrink-0">
              <h3 className="text-sm font-semibold text-foreground">
                {t("merge_modules.selectedForMerge")}
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t("mergeModal.totalCards", { count: totalCards })}
              </p>
            </div>
            <ul className="flex-1 overflow-y-auto divide-y divide-border min-h-0 max-h-[280px]">
              {selectedModules.length === 0 ? (
                <li className="p-4 text-center text-sm text-muted-foreground">
                  {t("mergeModal.addFromRight")}
                </li>
              ) : (
                selectedModules.map((deck) => (
                  <li
                    key={deck.id}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors"
                  >
                    <span className="shrink-0 flex items-center justify-center w-8 h-8 rounded-lg bg-muted/50">
                      {deck.emoji ? (
                        <span className="text-lg leading-none">{deck.emoji}</span>
                      ) : (
                        <Library size={18} className="text-muted-foreground" />
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate text-sm">{deck.title}</div>
                      <div className="text-xs text-muted-foreground">
                        {deck.cards?.length ?? 0} {t("mergeModal.cards")}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeModule(deck.id)}
                      className="shrink-0 rounded-full p-2 bg-destructive/15 text-destructive hover:bg-destructive/25 hover:scale-105 transition-colors transition-transform"
                      aria-label={t("mergeModal.remove")}
                    >
                      <Minus size={18} strokeWidth={2.5} />
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>

          {/* Right: Available modules */}
          <div className="flex flex-col rounded-xl border border-border bg-muted/20 overflow-hidden min-h-[320px]">
            <div className="px-4 py-3 border-b border-border bg-muted/30 shrink-0 space-y-2">
              <h3 className="text-sm font-semibold text-foreground">
                {t("merge_modules.availableModules")}
              </h3>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <input
                  type="text"
                  value={availableSearch}
                  onChange={(e) => setAvailableSearch(e.target.value)}
                  placeholder={t("merge_modules.searchPlaceholder")}
                  className="w-full rounded-lg border border-input bg-background pl-9 pr-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
                />
              </div>
            </div>
            <ul className="flex-1 overflow-y-auto divide-y divide-border min-h-0 max-h-[220px]">
              {availableModulesFiltered.length === 0 ? (
                <li className="p-4 text-center text-sm text-muted-foreground">
                  {availableSearch.trim()
                    ? t("mergeModal.noMatches")
                    : t("mergeModal.noOtherDecks")}
                </li>
              ) : (
                availableModulesFiltered.map((deck) => (
                  <li key={deck.id}>
                    <button
                      type="button"
                      onClick={() => addModule(deck)}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/50 transition-colors"
                    >
                      <span className="shrink-0 flex items-center justify-center w-8 h-8 rounded-lg bg-muted/50">
                        {deck.emoji ? (
                          <span className="text-lg leading-none">{deck.emoji}</span>
                        ) : (
                          <Library size={18} className="text-muted-foreground" />
                        )}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate text-sm">{deck.title}</div>
                        <div className="text-xs text-muted-foreground">
                          {deck.cards?.length ?? 0} {t("mergeModal.cards")}
                        </div>
                      </div>
                      <span className="shrink-0 rounded-full p-2 bg-primary/20 text-primary hover:bg-primary/30 hover:scale-105 transition-colors transition-transform">
                        <Plus size={18} strokeWidth={2.5} />
                      </span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>

        {/* Optional title for merged module */}
        <div className="space-y-1.5">
          <label htmlFor="merge-title" className="text-sm font-medium text-foreground">
            {t("merge_modules.newModuleName")}
          </label>
          <input
            id="merge-title"
            type="text"
            value={mergeTitle}
            onChange={(e) => setMergeTitle(e.target.value)}
            placeholder={t("mergeModal.newModuleNamePlaceholder")}
            className="w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          />
        </div>

        <DialogFooter className="flex flex-row gap-2 sm:gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted"
          >
            {t("mergeModal.cancel")}
          </button>
          <button
            type="button"
            onClick={handleMerge}
            disabled={selectedModules.length === 0 || isMerging}
            className="rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <BookOpen size={16} className="shrink-0" />
            {isMerging ? t("common.loading") : t("mergeModal.merge")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
